import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { useState, useEffect } from "react";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { ReportsTableMapper } from "./ReportsColumns";
import { VerificationAgencyService } from "../../../../Component/Api/VerificationAgency/VerificationAgencyService";
import { EEReportService } from "../../../../Component/Api/Report/ReportService";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { BBBEEScorecardAtom } from "../../../../State/BBBEEScorecard.Atom";
import { ReportGeneration } from "../../../../Component/Model/Dto/EmploymentEquity/Reporting";
import { NoDataScreen } from "../../../../Component/Common/Components/NoDataScreen/NoDataScreen";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { IntermediateGlobalLoading } from "../../../../Component/Common/Components/Loading/IntermediateGlobalLoading";
import { UserAtom } from '../../../../State/user.Atom';
import { useParams } from "react-router-dom";

interface RouteParams {
  BBBEEScorecardId: string;
}

export const Reports = () => {
    const { BBBEEScorecardId } = useParams<RouteParams>();
    const [reportData, setReportData] = useState<ReportGeneration.ReportGenerationResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [companyName, setCompanyName] = useState<string>('');
    const scorecard = useRecoilValue(BBBEEScorecardAtom);
    const setBBBEEScorecard = useSetRecoilState(BBBEEScorecardAtom);
    const user = useRecoilValue(UserAtom);

    useEffect(() => {
        if (BBBEEScorecardId && BBBEEScorecardId !== scorecard.scorecardId) {
            setBBBEEScorecard(prev => ({
                ...prev,
                scorecardId: BBBEEScorecardId
            }));
        }
    }, [BBBEEScorecardId, scorecard.scorecardId, setBBBEEScorecard]);

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const handleDownloadReport = async (documentId: number) => {
        try {
            const response = await EEReportService.GetDocumentUrl(documentId);
            window.open(response.data);
        } catch (error) {
            console.log(error);
        }
    }

    const fetchData = async (scorecardId: string) => {
        setIsLoading(true);
        try {
            let response = await EEReportService.GetLatestPublishedReport(scorecardId);
            setReportData(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const fetchCompanyName = async () => {
        try {
            let response;
            if(user.isAdmin && !user.hasAuditRole){
                response = await VerificationAgencyService.GetVaDetailsLinkedToSubmittedScorecard(scorecard.scorecardId);
            }
            else{
                response = await VerificationAgencyService.GetVaDetails();
            }
            setCompanyName(response.data.vaName);
        } catch (error) { }
    }

    useEffect(() => {
        fetchData(scorecard.scorecardId);
        fetchCompanyName();
    }, [entriesPerPage, pageNumber]);

    const reportTableData = ReportsTableMapper(reportData, handleDownloadReport)

    return (
        <>
            <MDBox style={{ paddingBottom: "20px" }}>
                <MDTypography variant='h4' fontWeight='bold' >
                    Reports Generated by {companyName}
                </MDTypography>
            </MDBox>
            {
                reportData.length > 0 && <DataTable
                    table={reportTableData}
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    isLoading={isLoading}
                    canSearch={false}
                    manualSorting={false}
                    showEntriesPerPage={false}
                    showTotalEntries={false}
                    showBottomMargin={false}
                    isSorted={false}
                />
            }
            { isLoading &&
               <IntermediateGlobalLoading />
            }
            {
                !isLoading && reportData.length === 0 &&
                <NoDataScreen
                    subText1="No reports have been generated by your verification agency."
                    subText2="Feel free to reach out to your verification agency for more information." />
            }
        </>
    );
}